"use client";

import { useSession } from "@/components/SupabaseProvider";
import { useQuery } from "@tanstack/react-query";
import {
  isPrepaidBlockEligible,
  prepaidCardsBlockedFrom,
} from "../helpers/prepaidBlockExperiment";
import { useProfile } from "./useProfile";

// Whether this user's plan Checkout refuses prepaid cards (the block-prepaid
// arm of "checkout-block-prepaid", see helpers/prepaidBlockExperiment.ts).
// - Asks klap-api, which evaluates the flag on the profile id, so the notice
//   matches the Checkout restriction even when PostHog's client flags are
//   stale (right after sign-in) or blocked by the browser.
// - Only eligible users ask; that request is the experiment's exposure. Only
//   PricingCards mounts this hook, so an exposed user is one who saw the plans.
// Returns false until the answer arrives, and on any error.
export const usePrepaidCardsBlocked = (): boolean => {
  const session = useSession();
  const { data: profile } = useProfile();
  const enabled = !!session && isPrepaidBlockEligible(profile);

  const { data } = useQuery({
    enabled,
    queryKey: ["checkout-options", profile?.id],
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      const status = (error as Error & { status?: number }).status;
      return failureCount < 2 && !(status && status < 500);
    },
    queryFn: async () => {
      const r = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/billing/checkout-options`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session!.access_token}`,
          },
        },
      );
      if (!r.ok)
        throw Object.assign(
          new Error(`GET /billing/checkout-options failed (${r.status})`),
          { status: r.status },
        );
      return r.json();
    },
  });

  return enabled && prepaidCardsBlockedFrom(data);
};
